import { useCallback, useEffect, useState } from "react";
import { EVENTO_AGOTADAS, EVENTO_JORNADA_CAMBIO, hayAgotadasAbiertas, pedirJornada, pedirOferta } from "../helpers/suelta-api";

/** ¿Está abierta la pantalla `jornadas-agotadas`? La banda de /turno la usa para no repetir sus botones detrás del diálogo. */
export function useAgotadasAbiertas() {
  const [abierta, setAbierta] = useState(hayAgotadasAbiertas());
  useEffect(() => {
    const alCambiar = () => setAbierta(hayAgotadasAbiertas());
    window.addEventListener(EVENTO_AGOTADAS, alCambiar);
    alCambiar();
    return () => window.removeEventListener(EVENTO_AGOTADAS, alCambiar);
  }, []);
  return abierta;
}

/**
 * Estado de la jornada y oferta de sueltas del médico logueado (#1887, T23; patrón de `useWeeklyUsage.js`).
 *
 * Alimenta la banda de /turno, el diálogo de confirmación, la pantalla `jornadas-agotadas` y la compra.
 * Todo el cálculo vive en el backend (`GET /me/jornada/` y `GET /me/suelta/`): acá no se decide ninguna hora,
 * ningún saldo ni ningún precio.
 *
 * - El valor vive en el MÓDULO y las instancias montadas se suscriben (misma razón que `useWeeklyUsage`): la
 *   banda de escritorio, la de celular y los diálogos globales comparten UNA petición, no cuatro.
 * - Sin polling: se pide al montar, al terminar de generar (`smartdx:usage-changed`, agrupado), al volver a la
 *   pestaña, tras activar o comprar (`smartdx:jornada-changed`) y al cruzar el término de la ventana. Lo único
 *   que corre solo es un TIMER LOCAL hasta el próximo instante que importa (aviso de término y término), que no
 *   toca la red salvo para releer el estado cuando la ventana termina.
 * - Fail-silent: si el endpoint falla (backend viejo en la ventana de un deploy, 401, red) `jornada` queda null y
 *   la banda simplemente no se dibuja. La banda es informativa: jamás puede mostrarle un error a quien trabaja.
 *
 * `auto: false` (diálogos globales de `App.js`) lee el valor sin pedirlo al montar: en la pantalla de login no hay
 * sesión y pedirlo daría un 401 inútil. Esos componentes llaman `recargar()` cuando se abren.
 * Cubre R57, R59 — .claude/specs/jornada-suelta/
 */

let store = { jornada: null, oferta: null, cargado: false };
let ultimoAt = 0;
let seq = 0;
let enVuelo = null;
const suscriptores = new Set();
const VENTANA_MS = 30000;

const publicar = (parche) => {
  store = { ...store, ...parche };
  suscriptores.forEach((notificar) => notificar(store));
};

const esJornadaValida = (d) => Boolean(d) && typeof d === "object" && typeof d.abierta === "boolean";
const esOfertaValida = (d) => Boolean(d) && typeof d === "object" && typeof d.disponible === "boolean";

// Una sola petición en vuelo; una respuesta vieja que llega tarde no pisa a una más nueva.
const pedirTodo = () => {
  if (enVuelo) return enVuelo;
  const mio = ++seq;
  enVuelo = Promise.allSettled([pedirJornada(), pedirOferta()]).then(([j, o]) => {
    enVuelo = null;
    if (mio !== seq) return;
    ultimoAt = Date.now();
    publicar({
      cargado: true,
      jornada: j.status === "fulfilled" && esJornadaValida(j.value) ? j.value : null,
      // Sin oferta válida no hay chip ni compra: ante la duda no se ofrece plata.
      oferta: o.status === "fulfilled" && esOfertaValida(o.value) ? o.value : null,
    });
  });
  return enVuelo;
};

export const recargarJornada = () => {
  // El estado cambió por una acción del médico (activar, comprar): descarta lo que viene en vuelo.
  seq += 1;
  enVuelo = null;
  return pedirTodo();
};

// Solo la oferta (`GET /me/suelta/`): la pantalla de bloqueo y «Elegir plan» la piden para decidir si muestran «No quiero un
// plan» (R33) sin pedir también el estado de la jornada. Comparte el valor del módulo con la banda.
export const recargarOferta = () =>
  pedirOferta()
    .then((o) => publicar({ oferta: esOfertaValida(o) ? o : null }))
    .catch(() => publicar({ oferta: null }));

export function useOfertaSuelta({ auto = true } = {}) {
  const [oferta, setOferta] = useState(store.oferta);
  const [cargada, setCargada] = useState(Boolean(store.oferta));
  useEffect(() => {
    const alPublicar = (s) => setOferta(s.oferta);
    suscriptores.add(alPublicar);
    setOferta(store.oferta);
    let vivo = true;
    if (auto) {
      pedirOferta()
        .then((o) => esOfertaValida(o) && publicar({ oferta: o }))
        .catch(() => {})
        .finally(() => vivo && setCargada(true));
    }
    return () => {
      vivo = false;
      suscriptores.delete(alPublicar);
    };
  }, [auto]);
  return { oferta, cargada, recargar: recargarOferta };
}

/** El valor actual del módulo, sin suscribirse (para decidir en un callback, p. ej. `JornadaAvisosGate`). */
export const estadoActualDeJornada = () => store;

/** Solo para los tests: vuelve el módulo a su estado inicial. */
export const _reiniciarJornadaEstado = () => {
  store = { jornada: null, oferta: null, cargado: false };
  ultimoAt = 0;
  seq += 1;
  enVuelo = null;
};

export default function useJornadaEstado({ auto = true } = {}) {
  const [estado, setEstado] = useState(store);
  const [ahora, setAhora] = useState(() => new Date());

  const recargar = useCallback(() => recargarJornada(), []);

  useEffect(() => {
    suscriptores.add(setEstado);
    setEstado(store);
    return () => suscriptores.delete(setEstado);
  }, []);

  useEffect(() => {
    if (!auto) return undefined;
    if (!store.cargado || Date.now() - ultimoAt >= VENTANA_MS) pedirTodo();
    return undefined;
  }, [auto]);

  // Al volver a la pestaña y tras generar (evento de `get-client`, agrupado: "Generar turno" hace una llamada por cama).
  useEffect(() => {
    if (!auto) return undefined;
    let timer = null;
    const alVolver = () => {
      if (document.visibilityState === "visible" && Date.now() - ultimoAt >= VENTANA_MS) recargarJornada();
    };
    const alConsumir = () => {
      clearTimeout(timer);
      timer = setTimeout(() => recargarJornada(), 2500);
    };
    const alCambiar = () => recargarJornada();
    document.addEventListener("visibilitychange", alVolver);
    window.addEventListener("smartdx:usage-changed", alConsumir);
    window.addEventListener(EVENTO_JORNADA_CAMBIO, alCambiar);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", alVolver);
      window.removeEventListener("smartdx:usage-changed", alConsumir);
      window.removeEventListener(EVENTO_JORNADA_CAMBIO, alCambiar);
    };
  }, [auto]);

  // Timer local hasta el próximo instante que cambia lo que se ve: el aviso de término y el término mismo.
  // Al cruzar el término se relee el estado (la lista de opciones para activar otra la decide el backend).
  const jornada = estado.jornada;
  useEffect(() => {
    setAhora(new Date());
    if (!auto || !jornada?.abierta) return undefined;
    let timer = null;
    const programar = () => {
      const t = Date.now();
      const marcas = [jornada.aviso_cierre_desde, jornada.cierra_en]
        .filter(Boolean)
        .map((x) => new Date(x).getTime())
        .filter((x) => x > t);
      if (!marcas.length) return;
      const espera = Math.min(Math.min(...marcas) - t + 200, 2 ** 31 - 1);
      timer = setTimeout(() => {
        setAhora(new Date());
        if (jornada.cierra_en && new Date(jornada.cierra_en).getTime() <= Date.now()) {
          recargarJornada();
        } else {
          programar();
        }
      }, espera);
    };
    programar();
    return () => clearTimeout(timer);
  }, [auto, jornada]);

  return { jornada, oferta: estado.oferta, cargado: estado.cargado, ahora, recargar };
}
